import {format, formatDistanceToNowStrict} from 'date-fns';

type DateInput = Date | string | number | null | undefined;

/** Rendered in place of a timestamp that can't be parsed. */
const INVALID_DATE = '—';

// Bare YYYY-MM-DD strings are calendar dates, not instants. `new Date("2025-07-10")`
// parses them as UTC midnight, which renders as the previous day in any timezone
// west of UTC. Detect them and build a local-midnight Date instead.
const CALENDAR_DATE = /^\d{4}-\d{2}-\d{2}$/;

// The POS report builds `runTimestamp` from `toISOString()` with the dashes,
// colons, milliseconds and the trailing `Z` all stripped — `20260903T202450`.
// That is ISO 8601 *basic* format, which `new Date()` rejects outright. Expand
// it back to extended form and read it as UTC, the zone it was written in.
const COMPACT_TIMESTAMP = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})$/;

function toDate(date: DateInput): Date {
  if (date == null) return new Date(NaN);

  if (typeof date === 'string') {
    if (CALENDAR_DATE.test(date)) {
      const [y, m, d] = date.split('-').map(Number);
      return new Date(y, m - 1, d);
    }
    const compact = COMPACT_TIMESTAMP.exec(date);
    if (compact) {
      const [, y, mo, d, h, min, s] = compact;
      return new Date(`${y}-${mo}-${d}T${h}:${min}:${s}Z`);
    }
  }

  return new Date(date);
}

/**
 * date-fns throws `RangeError: Invalid time value` on an invalid Date, which
 * inside a render escapes as an unrecoverable route error — one malformed
 * timestamp anywhere in a response blanks the entire page. Formatting is
 * display-only, so an unreadable value degrades to a dash instead.
 */
function formatSafely(date: DateInput, pattern: string): string {
  const value = toDate(date);
  return Number.isNaN(value.getTime()) ? INVALID_DATE : format(value, pattern);
}

export function formatDate(date: DateInput): string {
  return formatSafely(date, 'MMM d, yyyy');
}

export function formatDateTime(date: DateInput): string {
  return formatSafely(date, 'MMM d, yyyy h:mm a');
}

export function formatRelative(date: DateInput): string {
  const value = toDate(date);
  return Number.isNaN(value.getTime())
    ? INVALID_DATE
    : formatDistanceToNowStrict(value, {addSuffix: true});
}

export function formatCurrency(
  value: number,
  currency = 'USD',
  locale = 'en-US'
): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).format(value);
}

export function formatNumber(value: number, locale = 'en-US'): string {
  return new Intl.NumberFormat(locale).format(value);
}

export function formatPercent(value: number, digits = 1): string {
  return `${value.toFixed(digits)}%`;
}
